import {
  ArrowDownIcon,
  ArrowUpIcon,
  isKST,
  isMobileAtom,
  useRecoil,
} from '@orcar/common-d2c';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import * as S from './style';

export type DropdownOptionType<ValueType> = {
  value: ValueType;
  text: string;
};

type DropdownOptionProps<ValueType> = {
  option: DropdownOptionType<ValueType>;
  onClick: Dispatch<SetStateAction<ValueType>> | (() => void);
  isOpen: boolean;
  isUserSelected?: boolean;
};

type DropdownProps<ValueType> = {
  options: Array<DropdownOptionType<ValueType>>;
  selectedValue: ValueType;
  onSelect: Dispatch<SetStateAction<ValueType>>;
  width?: string;
  direction?: 'top' | 'bottom';
  topPosition?: number;
  bottomPosition?: number;
  borderColor?: string;
};

const DropdownOption = <ValueType,>({
  option,
  onClick,
  isOpen,
  isUserSelected,
}: DropdownOptionProps<ValueType>) => {
  const { value, text } = option;

  return (
    <S.DropdownOptionWrapper
      isOpen={isOpen}
      onClick={(e) => {
        onClick(value);
        e.stopPropagation();
      }}
    >
      <S.DropdownOptionText isUserSelected={isUserSelected}>
        {text}
      </S.DropdownOptionText>
    </S.DropdownOptionWrapper>
  );
};

const Dropdown = <ValueType extends string | number>({
  width,
  options,
  selectedValue,
  onSelect,
  direction = 'bottom',
  topPosition,
  bottomPosition,
  borderColor,
}: DropdownProps<ValueType>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUserSelected, setIsUserSelected] = useState(true); // TODO: default 값을 지정하기 위해 false -> true로 변경
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);

  const hasOptions = options.length > 0;
  const selectedOption = options.find(
    (option) => option.value === selectedValue,
  ) as DropdownOptionType<ValueType>;
  const noOption = {
    value: -1,
    text: '선택 가능한 값이 없습니다.',
  } as DropdownOptionType<ValueType>;

  useEffect(() => {
    if (options.length > 0 && !selectedOption) {
      onSelect(options[0].value);
    }
  }, [options, selectedOption]);

  return (
    <S.Wrapper
      isOpen={isOpen}
      isKST={isKST}
      isMobile={isMobile}
      width={width}
      direction={direction}
      topPosition={topPosition}
      borderColor={borderColor}
    >
      {hasOptions ? (
        isOpen ? (
          options.map((option) => (
            <DropdownOption<ValueType>
              option={option}
              onClick={(value) => {
                onSelect(value);
                setIsUserSelected(true);
                setIsOpen(false);
              }}
              key={option.value}
              isOpen={isOpen}
            />
          ))
        ) : (
          <DropdownOption<ValueType>
            option={selectedOption || noOption}
            onClick={() => {
              setIsOpen(true);
            }}
            isOpen={isOpen}
            isUserSelected={isUserSelected}
          />
        )
      ) : (
        <DropdownOption<ValueType>
          option={noOption}
          onClick={() => {
            //
          }}
          isOpen={false}
        />
      )}
      <S.ArrowIconWrapper
        onClick={() => {
          if (!hasOptions) return;
          setIsOpen(!isOpen);
        }}
      >
        <S.ArrowIcon src={isOpen ? ArrowUpIcon : ArrowDownIcon} />
      </S.ArrowIconWrapper>
    </S.Wrapper>
  );
};

export { Dropdown };
